import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  PauseIcon,
  PlayIcon,
  XCircleIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import * as api from '../../services/api';
import ReleaseUploadModal from './ReleaseUploadModal';

interface Release {
  id: string;
  version: string;
  release_notes: string;
  binary_hash: string;
  binary_size: number;
  is_mandatory: boolean;
  is_active: boolean;
  published_at: string;
  channel: string;
  rollout_percentage: number;
  target_tenant_ids: string[];
  download_count: number;
  paused_at: string | null;
  deactivated_at: string | null;
  deactivated_by: string | null;
}

interface ReleaseStats {
  totalEligible: number;
  downloadStarted: number;
  updateConfirmed: number;
  updateFailed: number;
  pendingAgents: number;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ReleasesPage: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stats, setStats] = useState<ReleaseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [rolloutEdit, setRolloutEdit] = useState<number | null>(null);

  const selected = releases.find((r) => r.id === selectedId) || null;

  const loadReleases = useCallback(async () => {
    try {
      const data = await api.getAgentReleases();
      const list = Array.isArray(data) ? data : [];
      setReleases(list);
      // Auto-select first if nothing selected
      if (!selectedId && list.length > 0) setSelectedId(list[0].id);
    } catch (err) {
      console.error('Failed to load releases', err);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  const loadStats = useCallback(async () => {
    if (!selectedId) return;
    try {
      const data = await api.getReleaseStats(selectedId);
      setStats(data);
    } catch {
      setStats(null);
    }
  }, [selectedId]);

  useEffect(() => { loadReleases(); }, [loadReleases]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      loadReleases();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadReleases, loadStats]);

  const handlePauseResume = async () => {
    if (!selected) return;
    try {
      await api.pauseRelease(selected.id, !selected.paused_at);
      await loadReleases();
    } catch (err) {
      console.error('Failed to pause/resume', err);
    }
  };

  const handleDeactivate = async () => {
    if (!selected) return;
    try {
      await api.updateRelease(selected.id, { isActive: false });
      await loadReleases();
    } catch (err) {
      console.error('Failed to deactivate', err);
    }
  };

  const handleRolloutSave = async () => {
    if (!selected || rolloutEdit === null) return;
    try {
      await api.updateRelease(selected.id, { rolloutPercentage: rolloutEdit });
      setRolloutEdit(null);
      await loadReleases();
    } catch (err) {
      console.error('Failed to update rollout', err);
    }
  };

  const getStatusColor = (r: Release) => {
    if (r.deactivated_at) return 'text-red-400';
    if (r.paused_at) return 'text-yellow-400';
    if (r.is_active) return 'text-green-400';
    return 'text-white/40';
  };

  const getStatusLabel = (r: Release) => {
    if (r.deactivated_at) return 'Deactivated';
    if (r.paused_at) return 'Paused';
    if (r.is_active) return 'Active';
    return 'Inactive';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue" />
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]">
      {/* Left panel — release list */}
      <div className="w-96 flex-shrink-0 flex flex-col bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <h2 className="text-sm font-medium text-white">Agent Releases</h2>
          <div className="flex gap-2">
            <button
              onClick={() => loadReleases()}
              className="p-1.5 text-anchor-muted hover:text-white transition-colors rounded-lg hover:bg-white/[0.04]"
              title="Refresh"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-anchor-blue rounded-lg hover:bg-anchor-blue/80 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Publish
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {releases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-anchor-muted text-sm">
              <p>No releases yet</p>
              <p className="text-xs mt-1">Publish your first agent release</p>
            </div>
          ) : (
            releases.map((r) => (
              <button
                key={r.id}
                onClick={() => { setSelectedId(r.id); setRolloutEdit(null); }}
                className={`w-full text-left px-4 py-3 border-b border-white/[0.04] transition-colors ${
                  selectedId === r.id
                    ? 'bg-anchor-blue/[0.08] border-l-2 border-l-anchor-blue'
                    : 'hover:bg-white/[0.03]'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">v{r.version}</span>
                  <div className="flex items-center gap-2">
                    {r.is_mandatory && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded">
                        MANDATORY
                      </span>
                    )}
                    <span className={`text-[10px] ${getStatusColor(r)}`}>{getStatusLabel(r)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-anchor-muted">{formatDate(r.published_at)}</span>
                  <span className="text-xs text-anchor-muted">{r.channel}</span>
                </div>
                {/* Rollout bar */}
                <div className="mt-2 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-anchor-blue rounded-full transition-all"
                    style={{ width: `${r.rollout_percentage}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-[10px] text-anchor-muted">
                  <span>Rollout {r.rollout_percentage}%</span>
                  <span>{r.download_count} downloads</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right panel — detail */}
      <div className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-y-auto">
        {!selected ? (
          <div className="flex items-center justify-center h-full text-anchor-muted text-sm">
            Select a release to view details
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-medium text-white">v{selected.version}</h2>
                <p className="text-sm text-anchor-muted mt-1">
                  Published {formatDate(selected.published_at)} &middot; {selected.channel} &middot; {formatSize(selected.binary_size)}
                </p>
              </div>
              <span className={`text-sm font-medium ${getStatusColor(selected)}`}>
                {getStatusLabel(selected)}
              </span>
            </div>

            {/* Release Notes */}
            <div>
              <h3 className="text-sm font-medium text-anchor-muted mb-2">Release Notes</h3>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 text-sm text-white/80 whitespace-pre-wrap">
                {selected.release_notes || 'No release notes provided.'}
              </div>
            </div>

            {/* Stats */}
            {stats && (
              <div>
                <h3 className="text-sm font-medium text-anchor-muted mb-2">Rollout Stats</h3>
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { label: 'Eligible', value: stats.totalEligible, icon: null },
                    { label: 'Downloading', value: stats.downloadStarted, icon: null },
                    { label: 'Confirmed', value: stats.updateConfirmed, icon: CheckBadgeIcon },
                    { label: 'Failed', value: stats.updateFailed, icon: ExclamationTriangleIcon },
                    { label: 'Pending', value: stats.pendingAgents, icon: null },
                  ].map((s) => (
                    <div key={s.label} className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 text-center">
                      <p className="text-lg font-medium text-white">{s.value}</p>
                      <p className="text-xs text-anchor-muted mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rollout Controls */}
            {selected.is_active && !selected.deactivated_at && (
              <div>
                <h3 className="text-sm font-medium text-anchor-muted mb-3">Rollout Controls</h3>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 space-y-4">
                  {/* Pause / Resume */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">
                        {selected.paused_at ? 'Release is paused' : 'Release is active'}
                      </p>
                      {selected.paused_at && (
                        <p className="text-xs text-anchor-muted mt-0.5">
                          Paused {formatDate(selected.paused_at)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handlePauseResume}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                        selected.paused_at
                          ? 'text-green-400 bg-green-500/10 border border-green-500/20 hover:bg-green-500/20'
                          : 'text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 hover:bg-yellow-500/20'
                      }`}
                    >
                      {selected.paused_at ? (
                        <><PlayIcon className="h-3.5 w-3.5" /> Resume</>
                      ) : (
                        <><PauseIcon className="h-3.5 w-3.5" /> Pause</>
                      )}
                    </button>
                  </div>

                  {/* Rollout Percentage */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-white">
                        Rollout: {rolloutEdit !== null ? rolloutEdit : selected.rollout_percentage}%
                      </p>
                      {rolloutEdit !== null && (
                        <button
                          onClick={handleRolloutSave}
                          className="px-3 py-1 text-xs text-anchor-blue bg-anchor-blue/10 border border-anchor-blue/20 rounded-lg hover:bg-anchor-blue/20 transition-colors"
                        >
                          Save
                        </button>
                      )}
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={rolloutEdit !== null ? rolloutEdit : selected.rollout_percentage}
                      onChange={(e) => setRolloutEdit(Number(e.target.value))}
                      className="w-full accent-anchor-blue"
                    />
                    <div className="flex justify-between text-[10px] text-anchor-muted mt-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* Deactivate */}
                  <div className="pt-2 border-t border-white/[0.06]">
                    <button
                      onClick={handleDeactivate}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors"
                    >
                      <XCircleIcon className="h-3.5 w-3.5" />
                      Deactivate Release
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Deactivated info */}
            {selected.deactivated_at && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-sm text-red-400">
                  Deactivated {formatDate(selected.deactivated_at)}
                  {selected.deactivated_by && ` by ${selected.deactivated_by}`}
                </p>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-sm font-medium text-anchor-muted mb-2">Details</h3>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-anchor-muted">SHA-256</span>
                  <span className="text-white/60 font-mono text-xs">{selected.binary_hash?.substring(0, 16)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-anchor-muted">Size</span>
                  <span className="text-white/80">{formatSize(selected.binary_size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-anchor-muted">Mandatory</span>
                  <span className="text-white/80">{selected.is_mandatory ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-anchor-muted">Downloads</span>
                  <span className="text-white/80">{selected.download_count}</span>
                </div>
                {selected.target_tenant_ids?.length > 0 && (
                  <div className="col-span-2 flex justify-between">
                    <span className="text-anchor-muted">Targeted Tenants</span>
                    <span className="text-white/60 font-mono text-xs">{selected.target_tenant_ids.length} tenant(s)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <ReleaseUploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            loadReleases();
          }}
        />
      )}
    </div>
  );
};

export default ReleasesPage;
